import React, { useEffect, useRef, useState } from 'react';
import useAlert from '../../hooks/useAlert';
import Modal from '../Modal/Modal';
import OverwriteModal from './OverwriteModal';

type Props = {
	show: boolean;
	onClose: () => void;
	onSave: (name: string) => Promise<void>;
	title?: string;
	placeholder?: string;
	initialValue?: string;
	label?: string;
	checkDuplicate?: (name: string) => boolean;
};

function SaveModal({ show, onClose, onSave, title = "상자를 저장합니다", label="이름", placeholder = "저장할 이름을 입력하세요", initialValue = '', checkDuplicate }: Props) {
	const [saveName, setSaveName] = useState('');
	const [saveError, setSaveError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const Alert = useAlert();

	useEffect(() => {
		if (show) {
			setSaveName(initialValue);
			setSaveError('');
			setIsLoading(false);
			setShowOverwriteConfirm(false);
			// 모달이 열릴 때 포커스 설정
			setTimeout(() => {
				inputRef.current?.focus();
				// 텍스트 전체 선택
				inputRef.current?.select();
			}, 100);
		}
	}, [show, initialValue]);

	const handleClose = () => {
		// 로딩 중일 때는 모달을 닫지 못하게 방지
		if (isLoading) return;

		setSaveName('');
		setSaveError('');
		setIsLoading(false);
		setShowOverwriteConfirm(false);
		onClose();
	};

	const executeSave = async () => {
		setIsLoading(true);
		setSaveError('');

		try {
			await onSave(saveName.trim());
			handleClose();
		} catch (error: any) {
			if (error?.response?.data?.Code === 'Errors.Editor.DuplicatedPresetName') {
				setSaveError('이미 등록된 상자 이름입니다.');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = async () => {
		if (!saveName.trim()) {
			setSaveError('이름을 입력하세요.');
			Alert.error('이런, 뭔가 문제가 있는 것 같습니다.');
			return;
		}

		if (saveName.length > 32) {
			setSaveError('최대 32자(공백 포함)까지만 입력할 수 있습니다.');
			Alert.error('이런, 뭔가 문제가 있는 것 같습니다.');
			return;
		}

		// 중복 체크 함수가 있고, 중복인 경우 확인 모달 표시
		if (checkDuplicate && checkDuplicate(saveName.trim())) {
			setShowOverwriteConfirm(true);
			return;
		}

		executeSave();
	};

	const onSaveNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSaveName(e.target.value);
		setSaveError('');
	};

	const onSaveKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (isLoading) return; // 로딩 중일 때는 키보드 이벤트 무시
		
		if (e.key === 'Enter') {
			handleSubmit();
		} else if (e.key === 'Escape') {
			handleClose();
		}
	};

	// 덮어쓰기 확인 모달에서 뒤로가기
	const handleOverwriteCancel = () => {
		setShowOverwriteConfirm(false);
	};

	// 덮어쓰기 확인 후 저장 실행
	const handleOverwriteConfirm = () => {
		setShowOverwriteConfirm(false);
		executeSave();
	};

	// 덮어쓰기 확인 화면
	if (showOverwriteConfirm) {
		return (
			<OverwriteModal
				show={show}
				onClose={handleOverwriteCancel}
				onConfirm={handleOverwriteConfirm}
				itemName={saveName}
			/>
		);
	}

	return (
		<Modal
			show={show}
			close={handleClose}
		>
			<div className="save-modal">
				<div className="save-modal-header">
					<h1>{title}</h1>
				</div>
				<div className="save-modal-body">
					<fieldset>
						<label>
							<div className="save-modal-body-label">
								{label}
							</div>
							<div className="save-modal-body-input">
								<input
									ref={inputRef}
									value={saveName}
									type="text"
									placeholder={placeholder}
									onChange={onSaveNameChange}
									onKeyDown={onSaveKeyDown}
									disabled={isLoading}
								/>
							</div>
						</label>
						{
							saveError?.length !== 0 && (
								<div className="msg error">
									{saveError}
								</div>
							)
						}
					</fieldset>
				</div>
				<div className="btns">
					<button
						className="btn medium tertiary"
						onClick={handleClose}
						disabled={isLoading}
					>
						취소
					</button>
					<button
						className="btn medium secondary"
						onClick={handleSubmit}
						disabled={isLoading}
					>
						저장하기
					</button>
				</div>
			</div>
		</Modal>
	);
}

export default SaveModal;
